/**
 * Price Lists Service
 * Handles CRUD operations for price lists and price list articles
 */

import { supabase } from '../lib/supabase'
import type { Database } from '../lib/supabase'

type PriceList = Database['public']['Tables']['price_lists']['Row']
type PriceListInsert = Database['public']['Tables']['price_lists']['Insert']
type PriceListUpdate = Database['public']['Tables']['price_lists']['Update']
type PriceListArticle = Database['public']['Tables']['price_list_articles']['Row']
type PriceListArticleInsert = Database['public']['Tables']['price_list_articles']['Insert']

export interface PriceListWithCounts extends PriceList {
  article_count: number
  customer_count: number
  subcontractor_count: number
}

export interface PriceListArticleWithDetails extends PriceListArticle {
  article: {
    id: string
    name: string
    description: string | null
    category: string | null
    is_rut_applicable: boolean
    vat_rate: number
    unit: string
    type: 'product' | 'service' | 'hourly'
  }
}

export const priceListsService = {
  /**
   * Get all price lists with article, customer and subcontractor counts
   */
  async getAll(): Promise<PriceListWithCounts[]> {
    // Get price lists with article count
    const { data: priceLists, error: plError } = await supabase
      .from('price_lists')
      .select(`
        *,
        price_list_articles(count)
      `)
      .order('type')
      .order('is_default', { ascending: false })
      .order('name')

    if (plError) throw plError

    // Get customer counts per price list
    const { data: customerCounts, error: custError } = await supabase
      .from('customers')
      .select('price_list_id')
      .not('price_list_id', 'is', null)

    if (custError) throw custError

    // Get subcontractor counts per price list
    const { data: subcontractorCounts, error: subError } = await supabase
      .from('subcontractors')
      .select('price_list_id')
      .not('price_list_id', 'is', null)

    if (subError) throw subError

    // Build count maps
    const customerCountMap = new Map<string, number>()
    customerCounts?.forEach(c => {
      if (c.price_list_id) {
        customerCountMap.set(c.price_list_id, (customerCountMap.get(c.price_list_id) || 0) + 1)
      }
    })

    const subcontractorCountMap = new Map<string, number>()
    subcontractorCounts?.forEach(s => {
      if (s.price_list_id) {
        subcontractorCountMap.set(s.price_list_id, (subcontractorCountMap.get(s.price_list_id) || 0) + 1)
      }
    })

    return (priceLists || []).map(pl => {
      // Supabase returns count aggregation in different formats depending on version
      const priceListArticles = pl.price_list_articles as any
      let articleCount = 0
      if (Array.isArray(priceListArticles)) {
        articleCount = priceListArticles[0]?.count || 0
      } else if (priceListArticles?.count !== undefined) {
        articleCount = priceListArticles.count
      }

      return {
        ...pl,
        article_count: articleCount,
        customer_count: customerCountMap.get(pl.id) || 0,
        subcontractor_count: subcontractorCountMap.get(pl.id) || 0
      }
    })
  },

  /**
   * Get price lists by type
   */
  async getByType(type: 'customer' | 'subcontractor'): Promise<PriceList[]> {
    const { data, error } = await supabase
      .from('price_lists')
      .select('*')
      .eq('type', type)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('name')

    if (error) throw error
    return data || []
  },

  /**
   * Get default price list by type
   */
  async getDefault(type: 'customer' | 'subcontractor'): Promise<PriceList | null> {
    const { data, error } = await supabase
      .from('price_lists')
      .select('*')
      .eq('type', type)
      .eq('is_default', true)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  /**
   * Get single price list by ID
   */
  async getById(id: string): Promise<PriceList | null> {
    const { data, error } = await supabase
      .from('price_lists')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Create new price list
   */
  async create(priceList: PriceListInsert): Promise<PriceList> {
    const { data, error } = await supabase
      .from('price_lists')
      .insert([priceList])
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update price list
   */
  async update(id: string, updates: PriceListUpdate): Promise<PriceList> {
    const { data, error } = await supabase
      .from('price_lists')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Delete price list
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('price_lists')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  /**
   * Copy a price list with all its article prices
   */
  async copy(id: string, newName: string): Promise<PriceList> {
    // Get the original price list
    const original = await this.getById(id)
    if (!original) throw new Error('Prislista hittades inte')

    // Get all article prices for the original
    const articles = await this.getArticles(id)

    // Create the new price list (never default when copying)
    const newPriceList = await this.create({
      name: newName,
      description: original.description,
      type: original.type,
      is_default: false,
      is_active: original.is_active
    })

    // Copy all article prices
    if (articles.length > 0) {
      const prices = articles.map(a => ({
        articleId: a.article_id,
        priceExclVat: a.price_excl_vat
      }))
      await this.setArticlePrices(newPriceList.id, prices)
    }

    return newPriceList
  },

  /**
   * Copy prices from one price list to another (overwrites existing prices)
   */
  async copyPricesFrom(fromPriceListId: string, toPriceListId: string): Promise<void> {
    // Get all prices from the source list
    const { data: sourceArticles, error: fetchError } = await supabase
      .from('price_list_articles')
      .select('article_id, price_excl_vat')
      .eq('price_list_id', fromPriceListId)

    if (fetchError) throw fetchError

    if (!sourceArticles || sourceArticles.length === 0) {
      throw new Error('Källprislistan har inga priser att kopiera')
    }

    // Prepare records for upsert
    const records = sourceArticles.map(a => ({
      price_list_id: toPriceListId,
      article_id: a.article_id,
      price_excl_vat: a.price_excl_vat,
      updated_at: new Date().toISOString()
    }))

    // Upsert all prices to the target list
    const { error: upsertError } = await supabase
      .from('price_list_articles')
      .upsert(records, {
        onConflict: 'price_list_id,article_id'
      })

    if (upsertError) throw upsertError
  },

  /**
   * Check if a price list can be deleted
   * Returns error message if it cannot be deleted, null if it can
   */
  async canDelete(id: string): Promise<{ canDelete: boolean; reason?: string }> {
    // Get the price list
    const priceList = await this.getById(id)
    if (!priceList) return { canDelete: false, reason: 'Prislista hittades inte' }

    // Check if it's a default price list
    if (priceList.is_default) {
      return { canDelete: false, reason: 'Kan inte ta bort en standardprislista' }
    }

    // Check if it's linked to any customers
    const { count: customerCount, error: custError } = await supabase
      .from('customers')
      .select('*', { count: 'exact', head: true })
      .eq('price_list_id', id)

    if (custError) throw custError
    if (customerCount && customerCount > 0) {
      return {
        canDelete: false,
        reason: `Prislistan är kopplad till ${customerCount} kund${customerCount > 1 ? 'er' : ''}`
      }
    }

    // Check if it's linked to any subcontractors
    const { count: subCount, error: subError } = await supabase
      .from('subcontractors')
      .select('*', { count: 'exact', head: true })
      .eq('price_list_id', id)

    if (subError) throw subError
    if (subCount && subCount > 0) {
      return {
        canDelete: false,
        reason: `Prislistan är kopplad till ${subCount} trädgårdsmästare`
      }
    }

    return { canDelete: true }
  },

  /**
   * Get articles with prices for a price list
   */
  async getArticles(priceListId: string): Promise<PriceListArticleWithDetails[]> {
    // First get the price list articles
    const { data: priceData, error: priceError } = await supabase
      .from('price_list_articles')
      .select('*')
      .eq('price_list_id', priceListId)
      .order('created_at')

    if (priceError) throw priceError
    if (!priceData || priceData.length === 0) return []

    // Get the article IDs
    const articleIds = priceData.map(p => p.article_id)

    // Fetch the articles with all relevant columns
    const { data: articlesData, error: articlesError } = await supabase
      .from('articles')
      .select('id, name, description, category, is_rut_applicable, vat_rate, unit, type')
      .in('id', articleIds)

    if (articlesError) throw articlesError

    // Create a map for quick lookup
    const articlesMap = new Map(articlesData?.map(a => [a.id, a]) || [])

    // Combine the data
    return priceData.map(p => ({
      ...p,
      article: articlesMap.get(p.article_id) || {
        id: p.article_id,
        name: 'Okänd artikel',
        description: null,
        category: null,
        is_rut_applicable: false,
        vat_rate: 25,
        unit: 'st',
        type: 'service' as const
      }
    }))
  },

  /**
   * Set article price in price list (upsert)
   */
  async setArticlePrice(
    priceListId: string,
    articleId: string,
    priceExclVat: number
  ): Promise<PriceListArticle> {
    const { data, error } = await supabase
      .from('price_list_articles')
      .upsert({
        price_list_id: priceListId,
        article_id: articleId,
        price_excl_vat: priceExclVat,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'price_list_id,article_id'
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Set multiple article prices in price list (batch upsert)
   */
  async setArticlePrices(
    priceListId: string,
    prices: { articleId: string; priceExclVat: number }[]
  ): Promise<void> {
    const records: PriceListArticleInsert[] = prices.map(p => ({
      price_list_id: priceListId,
      article_id: p.articleId,
      price_excl_vat: p.priceExclVat
    }))

    const { error } = await supabase
      .from('price_list_articles')
      .upsert(records, {
        onConflict: 'price_list_id,article_id'
      })

    if (error) throw error
  },

  /**
   * Remove article from price list
   */
  async removeArticle(priceListId: string, articleId: string): Promise<void> {
    const { error } = await supabase
      .from('price_list_articles')
      .delete()
      .eq('price_list_id', priceListId)
      .eq('article_id', articleId)

    if (error) throw error
  },

  /**
   * Get price for an article in a specific price list
   * Falls back to default price list if not found
   */
  async getArticlePrice(
    priceListId: string | null,
    articleId: string,
    type: 'customer' | 'subcontractor'
  ): Promise<number | null> {
    // Try specific price list first
    if (priceListId) {
      const { data } = await supabase
        .from('price_list_articles')
        .select('price_excl_vat')
        .eq('price_list_id', priceListId)
        .eq('article_id', articleId)
        .single()

      if (data) return data.price_excl_vat
    }

    // Fall back to default price list
    const defaultList = await this.getDefault(type)
    if (defaultList) {
      const { data } = await supabase
        .from('price_list_articles')
        .select('price_excl_vat')
        .eq('price_list_id', defaultList.id)
        .eq('article_id', articleId)
        .single()

      if (data) return data.price_excl_vat
    }

    return null
  },

  /**
   * Get all price list prices for a specific article
   */
  async getArticlePricesAcrossLists(articleId: string): Promise<{
    priceList: PriceList
    price: number | null
  }[]> {
    // Get all active price lists
    const { data: priceLists, error: plError } = await supabase
      .from('price_lists')
      .select('*')
      .eq('is_active', true)
      .order('type')
      .order('is_default', { ascending: false })
      .order('name')

    if (plError) throw plError

    // Get all prices for this article
    const { data: prices, error: pricesError } = await supabase
      .from('price_list_articles')
      .select('price_list_id, price_excl_vat')
      .eq('article_id', articleId)

    if (pricesError) throw pricesError

    // Map prices to price lists
    const priceMap = new Map<string, number>()
    prices?.forEach(p => {
      priceMap.set(p.price_list_id, p.price_excl_vat)
    })

    return (priceLists || []).map(pl => ({
      priceList: pl,
      price: priceMap.get(pl.id) ?? null
    }))
  },

  /**
   * Set price for an article in a specific price list
   */
  async setArticlePriceInList(
    priceListId: string,
    articleId: string,
    priceExclVat: number | null
  ): Promise<void> {
    if (priceExclVat === null || priceExclVat <= 0) {
      // Remove price if null or zero
      const { error } = await supabase
        .from('price_list_articles')
        .delete()
        .eq('price_list_id', priceListId)
        .eq('article_id', articleId)

      if (error) throw error
    } else {
      // Upsert price
      const { error } = await supabase
        .from('price_list_articles')
        .upsert({
          price_list_id: priceListId,
          article_id: articleId,
          price_excl_vat: priceExclVat,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'price_list_id,article_id'
        })

      if (error) throw error
    }
  }
}

/**
 * Price calculation utilities
 */
export const priceCalculations = {
  /**
   * Calculate price including VAT
   */
  calculatePriceInclVat(priceExclVat: number, vatRate: number): number {
    return priceExclVat * (1 + vatRate / 100)
  },

  /**
   * Calculate VAT amount
   */
  calculateVatAmount(priceExclVat: number, vatRate: number): number {
    return priceExclVat * (vatRate / 100)
  },

  /**
   * Calculate price excluding VAT from price including VAT
   */
  calculatePriceExclVat(priceInclVat: number, vatRate: number): number {
    return priceInclVat / (1 + vatRate / 100)
  },

  /**
   * Format price with currency
   */
  formatPrice(price: number, unit?: string): string {
    const formatted = new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price)

    return unit ? `${formatted}/${unit}` : formatted
  },

  /**
   * Format VAT rate
   */
  formatVatRate(vatRate: number): string {
    return `${vatRate}%`
  }
}
